import { variantProvider } from '@/app'
import { Link, Text, Button, View, SocialButton, Image } from '@/components'
import { useSharedSettings } from '@/services'
import { useAuth } from '@/features/Auth/bridge/useAuth'
import { useLinkedInSignIn } from '@/features/Auth/components/LinkedInButton'
import { ProviderId } from '@/features/Auth/providers/types'
import { AnyFunction, arePropsEqual, useI18N } from '@codeleap/common'
import React, { ReactNode } from 'react'

type RedirectLinkProps = {
  to: string
  text: string
  textAction: string
}

type HeaderComponentProps = {
  img: string
  centered?: boolean
  children?: ReactNode
  imageStyle?: React.CSSProperties | {}
}

export const RedirectLink = (props: RedirectLinkProps) => {
  const { to, text, textAction } = props

  return (
    <Button
      // @ts-ignore
      component={Link}
      to={to}
      variants={[
        'color:neutral1',
        'alignSelfCenter',
        'gap:0.5',
        'noUnderline',
        'fullWidth',
        'large',
        'border-radius:rounded'
      ]}
    >
      {text} <Text component="span" variants={['textCenter', 'color:primary3']} text={textAction} />
    </Button>
  )
}

type RedirectModalProps = {
  text: string
  textAction: string
  onPress: AnyFunction
}

export const RedirectModal = (props: RedirectModalProps) => {
  const { onPress, text, textAction } = props
  return (
    <Button
      debugName="RedirectModal:button"
      variants={[
        'pill',
        'large',
        'row',
        'alignCenter',
        'justifyCenter',
        'marginTop:2',
        'gap:1',
        'neutral2',
        'alignSelfStart',
        'paddingHorizontal:2',
        'fullWidth'
      ]}
      onPress={onPress}
      css={styles.button}
    >
      <Text variants={['textCenter']} text={text} style={styles.buttonText} />
      <Text
        text={textAction}
        onPress={onPress}
        variants={['color:primary3', 'cursor:pointer']}
        style={styles.buttonText}
      />
    </Button>
  )
}

/**
 * The provider ids are the machine's; the remote toggle's keys are its own.
 *
 * `socialProviders` is shared settings the server sends, keyed the way it has always been —
 * `linkedIn`, not `linkedin` — so renaming the provider to match mobile cannot rename this.
 * The map is the seam between the two. See EPIC-005.
 */
const SETTINGS_KEY: Record<Exclude<ProviderId, 'email' | 'customToken'>, string> = {
  google: 'google',
  facebook: 'facebook',
  apple: 'apple',
  linkedin: 'linkedIn'
}

const SOCIAL_PROVIDERS = Object.keys(SETTINGS_KEY) as (keyof typeof SETTINGS_KEY)[]

export const SocialProviders = () => {
  const { t } = useI18N()

  const { settings } = useSharedSettings()
  const { signInWith, handleProviderPress } = useAuth()

  // LinkedIn is the one provider that cannot be started by a function call: its popup lives
  // in a hook. Everything after the popup is the same path as the other three.
  const linkedInLogin = useLinkedInSignIn(signInWith)

  function isEnabled(provider: keyof typeof SETTINGS_KEY): boolean {
    const key = SETTINGS_KEY[provider]

    if (!settings?.socialProviders) return true
    if (!Object.hasOwn(settings.socialProviders, key)) return true

    const value = settings.socialProviders[key] as string | boolean

    return value === true || value === 'true'
  }

  return (
    <View variants={['column', 'gap:2']}>
      <Text text={t('Login.social')} variants={['p3', 'alignSelfCenter']} />

      <View variants={['row', 'gap:2', 'alignCenter', 'justifyCenter', 'alignSelfCenter']}>
        {SOCIAL_PROVIDERS.filter(isEnabled).map(provider => (
          <SocialButton
            key={provider}
            platform={provider}
            handleSocial={() => (provider === 'linkedin' ? linkedInLogin() : handleProviderPress(provider))}
          />
        ))}
      </View>
    </View>
  )
}

const HeaderComponent = ({ img, centered = true, children, imageStyle = {} }: HeaderComponentProps) => {
  return (
    <View
      variants={[
        'fullWidth',
        'center',
        'backgroundColor:primary1',
        !centered && 'alignEnd',
        children ? 'column' : 'row'
      ]}
      style={styles.imageWrapper}
    >
      <Image source={img} objectFit="contain" css={[centered ? styles.imageCentered : styles.image, imageStyle]} />
      {children}
    </View>
  )
}

const Header = React.memo(HeaderComponent, (previous, next) => {
  return arePropsEqual(previous, next, {
    check: ['img']
  })
})

export const AuthFormComponents = {
  RedirectLink,
  RedirectModal,
  SocialProviders,
  Header
}

const IMAGE_HEIGHT = 250

const styles = variantProvider.createComponentStyle(
  theme => ({
    button: {
      backgroundColor: theme.colors.neutral2,

      '&:hover': {
        backgroundColor: theme.colors.neutral3
      }
    },
    buttonText: {
      fontWeight: '500'
    },
    imageWrapper: {
      height: IMAGE_HEIGHT,
      maxHeight: IMAGE_HEIGHT,

      [theme.media.down('small')]: {
        height: IMAGE_HEIGHT * 0.8,
        maxHeight: IMAGE_HEIGHT * 8
      }
    },
    imageCentered: {
      background: theme.colors.primary1,
      height: '60%'
    },
    image: {
      background: theme.colors.primary1,
      height: '90%'
    }
  }),
  true
)
